import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { Observable, forkJoin, map, throwError, tap, catchError, Subject } from 'rxjs';
import { IArea, AreaPerspective, IAPIListResponse, IListOptions, IProject } from '@newroom-connect/library/interfaces';

import { EntityService } from '../entity/entity.service';
import { LoggingService } from '../logging/logging.service';
import { CacheManagerService, createCacheOperators, CacheRegistry, ICacheOperators } from '../cache';

import { AreaElementService, IUpdateAreaElementInput } from './area-element.service';

export interface IAreaMarketJob {
  id: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ICreateAreaInput {
  projectId: string;
  title: string; // Will be distributed across all translations
  translations: { code: string; }[]; // The language codes which are used for i81n properties of the area
  perspective: AreaPerspective;
  templateId?: string; // The optional ID of the template to pre-populate the project data with
}

export interface IUpdateAreaInput {
  slug?: string;
  streamId?: string | null;
  enabled?: boolean;
  translations?: IUpdateAreaTranslationInput[];
  password?: string | null;
  areaElements?: IUpdateAreaElementInput[];
  marketIds?: string[];
  enableTextTranslation?: boolean;
  enableFileTranslation?: boolean;
}

export interface IUpdateAreaTranslationInput {
  code: string;
  title?: string;
  backgroundFileId?: string;
}

export interface IDuplicateAreaInput {
  title: string;
}

export interface IGenerateMarketChildrenInput {
  marketIds: string[];
  enableTranslation: boolean;
}

export interface IGenerateMarketChildrenResponse {
  jobId: string;
  sessionId: string;
  message: string;
  marketsCount: number;
}

export interface IInheritanceConfig {
  marketIds: string[];
  enableTextTranslation: boolean;
  enableFileTranslation: boolean;
}

export interface IUpdateAreaPayload {
  areaPayload: IUpdateAreaInput;
  inheritanceConfig?: IInheritanceConfig;
}

@Injectable({
  providedIn: 'root'
})
export class AreaService extends EntityService implements OnDestroy {
  private readonly logger = inject(LoggingService);
  private readonly cacheManager = inject(CacheManagerService);
  private readonly cacheRegistry = inject(CacheRegistry);

  // Cache operators created with factory pattern
  private readonly cacheOps: ICacheOperators = createCacheOperators(this.cacheManager);

  public static readonly AREA_DEFAULT_TITLE = 'Unknown Area';

  public readonly searchableProperties = ['slug', 'translations[].title'];

  // Current area signal
  public readonly currentArea = signal<IArea | null>(null);

  // TODO: Remove this signal and rely on the user service as a single source of truth for the current user id.
  // This is currently not set anywhere and therefore is always null, only used in page title strategy and could be causing issues.
  public readonly currentUserId = signal<string | null>(null);

  // A flag to indicate if the cache store should be used for locally store areas details.
  public readonly useCacheStore = signal<boolean>(false);

  private readonly destroy$ = new Subject<void>();

  /**
   * Check if the inheritance modal should be shown for the given project and area.
   * Modal should be shown only if project has markets and area is HQ (not a market-specific subarea).
   *
   * @param project
   * @param area
   *
   * @returns
   */
  public static shouldShowInheritanceModal(project: IProject | null, area: IArea | null): boolean {
    return !!(project && project.markets && project.markets.length > 0 && area && !area.marketId);
  }

  /**
   * Clean up resources when the service is destroyed.
   */
  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Gets an area by ID using cache-first approach.
   *
   * @param projectId The ID of the project containing the area.
   * @param areaId The unique identifier of the area to retrieve.
   *
   * @returns Observable of the area.
   */
  public getArea(projectId: string, areaId: string): Observable<IArea> {
    // Early exit if caching is disabled
    if (!this.useCacheStore()) {
      return this.fetchFromApi(projectId, areaId, 'id').pipe(
        tap(area => this.currentArea.set(area))
      );
    }

    // Use factory-created cache operators
    return this.cacheOps.withCacheUpdate(
      this.cacheOps.cacheOrFetch<IArea>(
        'areas',
        () => this.fetchFromApi(projectId, areaId, 'id'),
        { projectId, itemId: areaId }
      ),
      'areas'
    ).pipe(
      tap(area => this.currentArea.set(area))
    );
  }

  /**
   * Gets an area by slug using cache-first approach.
   *
   * @param projectId The ID of the project containing the area.
   * @param areaSlug The URL-friendly slug identifier of the area.
   *
   * @returns Observable of the area.
   */
  public getAreaBySlug(projectId: string, areaSlug: string): Observable<IArea> {
    // Early exit if caching is disabled
    if (!this.useCacheStore()) {
      return this.fetchFromApi(projectId, areaSlug, 'slug').pipe(
        tap(area => this.currentArea.set(area))
      );
    }

    // Use enhanced cacheOrFetch with slug lookup support
    return this.cacheOps.withCacheUpdate(
      this.cacheOps.cacheOrFetch<IArea>(
        'areas',
        () => this.fetchFromApi(projectId, areaSlug, 'slug'),
        { projectId, itemId: areaSlug, lookupType: 'slug' }
      ),
      'areas'
    ).pipe(
      tap(area => this.currentArea.set(area))
    );
  }

  /**
   * List areas for a project.
   *
   * @param projectId The ID of the project to list areas for.
   * @param options Optional filtering and pagination options.
   *
   * @returns Observable of the API list response.
   */
  public listAreas(projectId: string, options?: IListOptions): Observable<IAPIListResponse<IArea>> {
    return super.list<IArea>(`projects/${projectId}/areas`, undefined, options);
  }

  /**
   * Creates a new area in the specified project.
   *
   * @param input Configuration data for the new area.
   *
   * @returns Observable of the created area.
   */
  public createArea(input: ICreateAreaInput): Observable<IArea> {
    return this.apiService.post<IArea>(`projects/${input.projectId}/areas`, input);
  }

  /**
   * Updates an existing area with new data.
   *
   * @param projectId The ID of the project containing the area.
   * @param areaId The unique identifier of the area to update.
   * @param input The updated area data.
   * @param inheritanceConfig Optional configuration for pushing changes to child areas.
   *
   * @returns Observable of the updated area.
   */
  public updateArea(projectId: string, areaId: string, input: IUpdateAreaInput, inheritanceConfig?: IInheritanceConfig): Observable<IArea> {
    // Wrap payload in consistent structure
    const payload: IUpdateAreaPayload = {
      areaPayload: input,
      ...(inheritanceConfig && { inheritanceConfig })
    };

    const update$ = this.apiService.patch<IArea>(`projects/${projectId}/areas/${areaId}?ngsw-bypass=true`, payload).pipe(
      map(area => {
        // Hydrate the area elements with the media library files
        area.areaElements = AreaElementService.hydrateAreaElementsMediaLibrary(area.areaElements);
        area.areaElements = AreaElementService.hydrateAreaElementsBackgroundFiles(area.areaElements);

        // Update current area signal if this is the current area
        if (this.currentArea()?.id === area.id) {
          this.currentArea.set(area);
        }

        return area;
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );

    // Conditionally update cache
    if (!this.useCacheStore()) {
      return update$;
    }

    return this.cacheOps.withCacheUpdate(update$, 'areas');
  }

  /**
   * Delete an area from a given project.
   *
   * @param projectId
   * @param areaId
   *
   * @returns Observable of the deleted area.
   */
  public deleteArea(projectId: string, areaId: string): Observable<IArea> {
    const delete$ = this.deleteFromApi(projectId, areaId);

    if (!this.useCacheStore()) {
      return delete$;
    }

    return this.cacheOps.withCacheRemoval(delete$, 'areas', areaId);
  }

  /**
   * Delete multiple areas.
   *
   * @param projectId
   * @param areaIds Array of area IDs to delete.
   *
   * @returns Observable of the deleted areas.
   */
  public deleteAreas(projectId: string, areaIds: string[]): Observable<IArea[]> {
    const delete$ = forkJoin(areaIds.map(areaId => this.deleteFromApi(projectId, areaId)));

    if (!this.useCacheStore()) {
      return delete$;
    }

    return this.cacheOps.withCacheMultiRemoval(delete$, 'areas', areaIds);
  }

  /**
   * Duplicate an area with a diffrenet name.
   *
   * @param projectId
   * @param areaId The area ID to duplicate.
   * @param input Duplication input data.
   *
   * @returns Observable of the duplicated area.
   */
  public duplicateArea(projectId: string, areaId: string, input: IDuplicateAreaInput): Observable<IArea> {
    return this.apiService.post<IArea>(`projects/${projectId}/areas/${areaId}/duplicate`, input);
  }

  /**
   * Fetch area from API only - no caching or side effects.
   *
   * @param projectId
   * @param identifier The area ID or slug.
   * @param type Whether the identifier is an 'id' or 'slug'.
   *
   * @returns Observable of the area.
   */
  public fetchFromApi(projectId: string, identifier: string, type: 'id' | 'slug'): Observable<IArea> {
    const apiUrl = type === 'id'
      ? `projects/${projectId}/areas/${identifier}`
      : `projects/${projectId}/areas/area-slug/${identifier}`;

    return this.apiService.get<IArea>(apiUrl).pipe(
      map(area => {
        // Hydrate area elements
        area.areaElements = AreaElementService.hydrateAreaElementsMediaLibrary(area.areaElements);
        area.areaElements = AreaElementService.hydrateAreaElementsBackgroundFiles(area.areaElements);

        this.logger.debug('Area fetched from API');
        return area;
      }),
      catchError(error => {
        this.logger.error(`Error fetching area with ${type} ${identifier}:`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete area from API only - no caching or side effects.
   *
   * @param projectId
   * @param areaId The area ID to delete.
   *
   * @returns Observable of the deleted area.
   */
  private deleteFromApi(projectId: string, areaId: string): Observable<IArea> {
    return this.apiService.delete<IArea>(`projects/${projectId}/areas/${areaId}`).pipe(
      tap(() => {
        // Clear current area if this was the current area
        if (this.currentArea()?.id === areaId) {
          this.currentArea.set(null);
        }
      }),
      catchError(error => {
        this.logger.error(`Error deleting area ${areaId}:`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Generate market-specific child areas for a specific area.
   * Creates child areas for the given area based on selected markets.
   *
   * @param projectId The ID of the project containing the area.
   * @param areaId The ID of the area to generate market children for.
   * @param input The market IDs and translation settings.
   *
   * @returns Observable of the generation job response.
   */
  public generateMarketChildren(projectId: string, areaId: string, input: IGenerateMarketChildrenInput): Observable<IGenerateMarketChildrenResponse> {
    return this.apiService.post<IGenerateMarketChildrenResponse>(`projects/${projectId}/areas/${areaId}/generate-market-sub-areas`, input);
  }

  /**
   * Get active market generation jobs for a given project id.
   *
   * @param projectId
   * @param options
   *
   * @returns Observable of active market generation jobs.
   */
  public getActiveMarketGenerationJobs(projectId: string, options?: IListOptions): Observable<IAPIListResponse<IAreaMarketJob>> {
    return this.apiService.list<IAPIListResponse<IAreaMarketJob>>(`projects/${projectId}/areas/market-generation/active-jobs`, undefined, options);
  }

  /**
   * Initialize the area cache store for a given project id.
   *
   * @param projectId
   *
   * @returns Observable that emits true when the area cache is initialized.
   */
  public initializeCache(projectId: string): Observable<boolean> {
    this.logger.debug(`Initializing area cache for project ${projectId}`);

    this.useCacheStore.set(true);

    // No need to check if the cache is already initialized, cache registry already handles this per project id.
    return this.cacheRegistry.initialize(EntityService.baseUrl, projectId);
  }
}
